import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik"
import { CommunityOpenDiscussCreate } from "../models/CommunityOpenDiscussCreate"
import { CommunityInput } from "./CommunityInput"
import { CommunityTagInput } from "./CommunityTagInput"
import * as Yup from 'yup'
import { useSuggestTags } from "../../../hooks/community/useSuggestTags"
import { useEffect, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useOpenDiscussionId } from "../../../hooks/community/useOpenDiscussionId"
import { LoaderOverlay } from "../../../hooks/loader/LoaderOverlay"
import { CommunityTagModel } from "../models/CommunityTagModel"

const init: CommunityOpenDiscussCreate = {
    topic: '',
    description: '',
    tags: []
}

const validationSchema = Yup.object({
    topic: Yup.string().required('Topic is required').max(100),
    description: Yup.string().required('Description is required'),
    tags: Yup.array()
            .min(1, "Should contain at least one tag")
            .required("Tag is required"),
});

type Props = {
    onSubmit: (value: CommunityOpenDiscussCreate, id?: number | string) => void;
    redirectOnFailed?: string;
}

export const CommunityOpenDiscussForm = ({ onSubmit, redirectOnFailed }: Props) => {
    const { id } = useParams() as any;
    const history = useHistory();
    const { data, loading, error } = useOpenDiscussionId(id);
    const [tagInput, setTagInput] = useState('');
    const { tags } = useSuggestTags(tagInput);
    const [initialValues, setInitialValues] = useState(init);

    useEffect(() => {
        if(!!error)
            return history.replace(redirectOnFailed ?? '/community');
    }, [error]);

    useEffect(() => {
        if(!!data) {
            setInitialValues({ 
                topic: data.topic, 
                description: data.description, 
                tags: (data.tags.map(t => ({ label: t, value: t }))  as CommunityTagModel[]) 
            });
        }
    }, [data]);

    return (
        <>
            <LoaderOverlay visible={loading} message={'Loading...'} />
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(f) => onSubmit(f, id)}
                validationSchema={validationSchema}
            >
                {() => (
                    <Form>
                        <div className="community-open-discuss-form">
                            <div className="community-form__info">
                                <div className="community-metadata">
                                    <div>
                                        <h4>
                                            What would you like to share?
                                        </h4>
                                        <p style={{ margin: '0', marginTop: '10px'}}>
                                            Share insights and collaborate with peers across the community, fostering vibrant exchanges on topics that matter. 
                                        </p>
                                    </div>
                                    <div className="d-flex flex-column w-100" style={{ gap: '30px' }}>
                                        <div className="w-100">
                                            <Field name="topic">
                                                {({ field, form }: FieldProps) => (
                                                    <>
                                                        <CommunityInput
                                                            type="line"
                                                            title="Topic"
                                                            description="Title your discussion"
                                                            limit={100}
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                        />
                                                        <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                                    </>
                                                )}
                                            </Field> 
                                        </div>

                                        <div className="w-100">
                                            <Field name="description"> 
                                                {({ field, form }: FieldProps) => (
                                                    <>
                                                        <CommunityInput
                                                            type="rich"
                                                            title="description"
                                                            description="Share what matters to you, spark ideas, and invite peers to contribute their perspectives."
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                        />
                                                        <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="w-100">
                                    <Field name="tags">
                                            {({ field, form }: FieldProps) => (
                                                <>
                                                    <CommunityTagInput
                                                        title="Tags"
                                                        description="Choose tags that highlight the key points."
                                                        value={field.value}
                                                        onChange={(value) => form.setFieldValue(field.name, value)}
                                                        onChangeInput={setTagInput}
                                                        suggests={tags.results.map(t => t.name)}
                                                        placeholder="Type and press Enter..."
                                                    />
                                                    <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                                </>
                                            )}
                                    </Field>
                                </div>
                                
                            </div>
                            <button type="submit">
                                {id ? 'Save changes' : 'Start discussion'}
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                    <path d="M8.5 0.378906L12 3.38306H0" stroke="white"/>
                                </svg>
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    )
}